/**
 * Duel between two real players (data/pvpDuel.ts). The host (the zone server) puts both in one World, parks that
 * World's primary slot, adds the two with `addPlayer` and calls `begin(a, b)`. Here: 3-2-1, the fight (the two may hurt
 * only each other: `World.pvpBout` feeds `isHostile`), over at a tenth of the health, out of the ring, or when time is up
 * (the share of health left decides). Nobody dies and nothing is lost: both are healed at the result. The host sends
 * them back once `over`. Vs-NPC duels stay on the ring's `Duel` (systems/duelGround.ts); this is the `pvp` mode.
 */
import { DUEL_GROUND, inRing } from '../data/duelGround';
import { PVP_DUEL } from '../data/pvpDuel';
import type { Entity } from '../world';
import { duelGroundOf } from './duelGround';
import type { SystemHost, WorldSystem } from './types';

export type PvpDuelPhase = 'invite' | 'declined' | 'countdown' | 'fight' | 'result' | 'roundEnd' | 'parry' | 'counter' | 'stance' | 'guard';

/** Event (part of `WorldEvent`): the bout's stages; `a` challenged `b`; `winner` is an entity id at the result. */
export type PvpDuelEvent = {
  type: 'pvpDuel';
  phase: PvpDuelPhase;
  a: number;
  b: number;
  names: [string, string];
  winner?: number | null;
  reason?: string;
  /** Best-of-three bouts (systems/duelMatch.ts, round 2): the round, rounds won [a, b], the ring (x, y, radius), a parry by this id. */
  round?: number;
  wins?: [number, number];
  ring?: [number, number, number];
  parry?: number;
  /** Round 3: guard points [a, b], seconds of broken guard [a, b], a counter stance's window (s), by `parry` (its id). */
  gp?: [number, number];
  broken?: [number, number];
  window?: number;
};

/** Answer a challenge (the server handles it; a single-player World has none to answer). */
export type PvpDuelCommand = { t: 'duelAnswer'; accept: boolean };

const SYSTEMS = new WeakMap<object, PvpDuelSystem>();
export const pvpDuelOf = (w: object): PvpDuelSystem | null => SYSTEMS.get(w) ?? null;

interface Bout {
  a: number;
  b: number;
  phase: 'countdown' | 'fight' | 'result';
  t: number;
  elapsed: number;
  winner: number | null;
}

export class PvpDuelSystem implements WorldSystem {
  readonly name = 'pvpDuel';
  private bout: Bout | null = null;
  private done = false;

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    duelAnswer: () => this.h.notice('ไม่มีคำท้าให้ตอบ'),
  };

  setup(): void {
    SYSTEMS.set(this.h.world, this);
  }

  /** A bout is on (countdown, fight or result). */
  get active(): boolean {
    return this.bout !== null;
  }

  /** The result has been shown: the host may send both back. */
  get over(): boolean {
    return this.done;
  }

  get state(): Readonly<Bout> | null {
    return this.bout;
  }

  /** Sets both on the ring's marks and starts the countdown. False when it cannot be (a bout is on, a body is missing). */
  begin(aId: number, bId: number): boolean {
    const w = this.h.world;
    const a = w.get(aId);
    const b = w.get(bId);
    if (this.bout || aId === bId || a?.kind !== 'player' || b?.kind !== 'player') return false;
    // The ring is for the two of them: no monsters about.
    for (const e of [...w.entities.values()]) if (e.kind === 'monster') w.entities.delete(e.id);
    const ring = duelGroundOf(w)?.ring;
    const spawn = w.map.spawn;
    const [ma, mb] = ring ? ring.marks : [[spawn.x - 8, spawn.y] as const, [spawn.x + 8, spawn.y] as const];
    for (const [e, m] of [[a, ma], [b, mb]] as const)
      Object.assign(e, { x: m[0], y: m[1], z: 0, vz: 0, path: null, cast: null, targetId: null, blocking: false });
    this.h.face(a, b);
    this.h.face(b, a);
    this.bout = { a: aId, b: bId, phase: 'countdown', t: PVP_DUEL.countdown, elapsed: 0, winner: null };
    this.done = false;
    w.pvpBout = { a: aId, b: bId, fight: false };
    this.say('countdown');
    return true;
  }

  /** `id` gives the bout away (left the ring, dropped, gave up): the other wins. */
  forfeit(id: number): void {
    const o = this.bout;
    if (!o || o.phase === 'result' || (id !== o.a && id !== o.b)) return;
    this.finish(id === o.a ? o.b : o.a);
  }

  private say(phase: PvpDuelPhase): void {
    const o = this.bout!;
    const w = this.h.world;
    this.h.emit({
      type: 'pvpDuel',
      phase,
      a: o.a,
      b: o.b,
      names: [w.get(o.a)?.name ?? '', w.get(o.b)?.name ?? ''],
      ...(phase === 'result' ? { winner: o.winner } : {}),
    });
  }

  private finish(winner: number | null): void {
    const o = this.bout!;
    const w = this.h.world;
    Object.assign(o, { phase: 'result', t: PVP_DUEL.result, winner });
    w.pvpBout = null;
    // Nobody dies, nothing is lost: both whole again.
    for (const id of [o.a, o.b]) {
      const e = w.get(id);
      if (!e) continue;
      Object.assign(e, { hp: e.maxHp, qi: e.maxQi, dead: false, deadT: 0, cast: null, blocking: false, path: null });
    }
    this.say('result');
  }

  step(dt: number): void {
    const o = this.bout;
    const w = this.h.world;
    if (!o) return;
    if (o.phase === 'countdown') {
      if ((o.t -= dt) > 0) return;
      o.phase = 'fight';
      o.t = PVP_DUEL.maxSeconds;
      if (w.pvpBout) w.pvpBout.fight = true;
      return this.say('fight');
    }
    if (o.phase === 'result') {
      if ((o.t -= dt) > 0) return;
      this.bout = null;
      this.done = true;
      return;
    }
    o.elapsed += dt;
    const a = w.get(o.a);
    const b = w.get(o.b);
    if (!a || !b) return this.finish(a ? o.a : b ? o.b : null);
    const ring = duelGroundOf(w)?.ring;
    const lost = (e: Entity): boolean => e.hp <= e.maxHp * DUEL_GROUND.floorHp || (!!ring && !inRing(ring, e));
    const la = lost(a);
    const lb = lost(b);
    const share = (e: Entity): number => e.hp / e.maxHp;
    if (la || lb) return this.finish(la && lb ? (share(a) >= share(b) ? o.a : o.b) : la ? o.b : o.a);
    if ((o.t -= dt) <= 0) this.finish(share(a) === share(b) ? null : share(a) > share(b) ? o.a : o.b);
  }
}
